import { useEffect, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useCardStore } from '@/stores/useCardStore'
import {
  fetchCard,
  EDGE_TYPES,
  EDGE_TYPE_LABELS,
} from '@/lib/api'
import type { Edge, CardDetail as CardDetailType, EdgeType } from '@/lib/api'
import { TypeBadge, DomainBadge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import {
  ArrowLeft,
  Loader2,
  FileText,
  Link as LinkIcon,
} from 'lucide-react'

export function CardDetail() {
  const { cardId } = useParams<{ cardId: string }>()
  const {
    selectedCard,
    isLoadingCard,
    setSelectedCard,
    setLoadingCard,
    setError,
  } = useCardStore()

  useEffect(() => {
    if (!cardId) return

    setLoadingCard(true)
    fetchCard(cardId)
      .then(setSelectedCard)
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Failed to load card')
      })
      .finally(() => setLoadingCard(false))

    return () => setSelectedCard(null)
  }, [cardId, setSelectedCard, setLoadingCard, setError])

  if (isLoadingCard) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!selectedCard) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <p className="text-muted-foreground mb-4">Card not found</p>
        <Button variant="outline" asChild>
          <Link to="/cards">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Library
          </Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      <CardInfoSection card={selectedCard} />
      <LinkTablesSection
        incomingEdges={selectedCard.incoming_edges}
        outgoingEdges={selectedCard.outgoing_edges}
      />
    </div>
  )
}

function CardInfoSection({ card }: { card: CardDetailType }) {
  // Calculate link counts by type
  const linkCounts = useMemo(() => {
    const counts: Record<string, { incoming: number; outgoing: number }> = {}

    card.outgoing_edges.forEach((edge) => {
      if (!counts[edge.type]) counts[edge.type] = { incoming: 0, outgoing: 0 }
      counts[edge.type].outgoing++
    })

    card.incoming_edges.forEach((edge) => {
      if (!counts[edge.type]) counts[edge.type] = { incoming: 0, outgoing: 0 }
      counts[edge.type].incoming++
    })

    return counts
  }, [card.incoming_edges, card.outgoing_edges])

  // Format link counts for display
  const linkCountDisplay = useMemo(() => {
    const parts = Object.entries(linkCounts).map(([type, counts]) => {
      const total = counts.incoming + counts.outgoing
      const label = EDGE_TYPE_LABELS[type as EdgeType]
      return `${total} ${label?.toLowerCase() || type.replace('_', ' ')}`
    })
    return parts.join(', ')
  }, [linkCounts])

  const totalEdges = card.incoming_edges.length + card.outgoing_edges.length

  // Merge card tags with source tags (deduplicated)
  const allTags = useMemo(() => {
    const tagSet = new Set<string>()
    // Add card's AI-generated tags
    card.tags?.forEach((tag) => tagSet.add(tag))
    // Add source's user-provided tags
    card.sources.forEach((source) => {
      source.tags?.forEach((tag) => tagSet.add(tag))
    })
    return Array.from(tagSet)
  }, [card.tags, card.sources])

  return (
    <header className="border-b border-border pl-6 pr-8 md:pl-8 md:pr-12 lg:pl-12 lg:pr-20 py-6 bg-card">
      {/* Back button */}
      <div className="flex items-center gap-4 mb-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/cards">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Library
          </Link>
        </Button>
      </div>

      {/* Card Title */}
      {card.title && <h1 className="text-2xl font-medium mb-3">{card.title}</h1>}

      {/* Badges: Type, Domain */}
      <div className="flex items-center gap-2 mb-4">
        <TypeBadge type={card.type} />
        <DomainBadge domain={card.domain} />
      </div>

      {/* Full Summary */}
      <p className="text-base leading-relaxed mb-4">{card.summary}</p>

      {/* Tags (merged from card + source) */}
      {allTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {allTags.map((tag) => (
            <span
              key={tag}
              className="text-xs text-muted-foreground"
            >
              #{tag}
            </span>
          ))}
        </div>
      )}

      {/* Link counts */}
      {totalEdges > 0 && (
        <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
          <LinkIcon className="h-4 w-4" />
          <span>{linkCountDisplay}</span>
        </div>
      )}

      {/* Source and Created date */}
      <div className="flex items-center gap-6 text-sm text-muted-foreground">
        {card.sources.length > 0 && (
          <Link
            to={`/sources/${card.sources[0].source_id}`}
            className="flex items-center gap-1 hover:text-foreground transition-colors"
          >
            <FileText className="h-4 w-4" />
            <span className="hover:underline">
              Source: {card.sources[0].title || card.sources[0].filename || 'Untitled'}
            </span>
          </Link>
        )}
        <span>Created: {new Date(card.created_at).toLocaleDateString()}</span>
      </div>
    </header>
  )
}

function LinkTablesSection({
  incomingEdges,
  outgoingEdges,
}: {
  incomingEdges: Edge[]
  outgoingEdges: Edge[]
}) {
  // Group all edges by type (combine incoming and outgoing)
  const edgesByType = useMemo(() => {
    const grouped: Record<string, Edge[]> = {}

    EDGE_TYPES.forEach((type) => {
      grouped[type] = []
    })

    ;[...incomingEdges, ...outgoingEdges].forEach((edge) => {
      if (grouped[edge.type]) {
        grouped[edge.type].push(edge)
      }
    })

    return grouped
  }, [incomingEdges, outgoingEdges])

  // Sort edges by weight descending
  const sortByWeight = (edges: Edge[]) =>
    [...edges].sort((a, b) => b.weight - a.weight)

  const hasAnyEdges = incomingEdges.length > 0 || outgoingEdges.length > 0

  return (
    <div className="flex-1 overflow-auto pl-6 pr-8 md:pl-8 md:pr-12 lg:pl-12 lg:pr-20 py-8 space-y-8">
      {!hasAnyEdges && (
        <div className="text-center text-muted-foreground py-8">
          No relationships found for this card
        </div>
      )}

      {EDGE_TYPES.map((type) => {
        const edges = edgesByType[type]
        if (edges.length === 0) return null

        const label = EDGE_TYPE_LABELS[type]

        return (
          <EdgeTable
            key={type}
            title={label}
            edges={sortByWeight(edges)}
          />
        )
      })}
    </div>
  )
}

function EdgeTable({
  title,
  edges,
}: {
  title: string
  edges: Edge[]
}) {
  return (
    <div className="space-y-2">
      <h3 className="text-base font-light flex items-center gap-2 border-b border-border pb-2">
        <LinkIcon className="h-4 w-4 text-muted-foreground" />
        {title}
        <span className="text-muted-foreground">({edges.length})</span>
      </h3>

      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm table-fixed">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-2 text-left font-medium w-16">Weight</th>
              <th className="px-4 py-2 text-left font-medium w-48">Title</th>
              <th className="px-4 py-2 text-left font-medium">Summary</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {edges.map((edge) => (
              <tr
                key={edge.edge_id}
                className="hover:bg-muted/30 transition-colors align-top"
              >
                <td className="px-4 py-3 text-muted-foreground">
                  {Math.round(edge.weight * 100)}%
                </td>
                <td className="px-4 py-3">
                  <Link
                    to={`/cards/${edge.linked_card_id}`}
                    className="text-primary hover:underline font-medium"
                  >
                    {edge.linked_card_title || 'Untitled'}
                  </Link>
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {edge.linked_card_summary}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
